// NEW COMPONENT - Smart FAQ System for HMIS
// This is a completely new addition and does not modify any existing UI

import { faqDatabase } from './faqDatabase.js';

// AI-like response generation
export class SmartFaqSystem {
  constructor() {
    this.intentPatterns = {
      greeting: ['hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening', 'namaste'],
      help: ['help', 'assist', 'support', 'guide'],
      registration: ['register', 'sign up', 'create account', 'new patient', 'patient registration'],
      appointment: ['appointment', 'schedule', 'book', 'visit'],
      search: ['find', 'search', 'look for', 'locate'],
      billing: ['bill', 'payment', 'cost', 'price', 'charge'],
      reports: ['report', 'result', 'document', 'print'],
      inventory: ['stock', 'medicine', 'inventory', 'store'],
      system: ['backup', 'restore', 'permissions', 'roles'],
      goodbye: ['bye', 'goodbye', 'see you', 'thanks', 'thank you']
    };

    this.responses = {
      greeting: [
        "Hello! Welcome to HMIS Smart Assistant. How can I help you today?",
        "Hi there! I'm your HMIS assistant. What would you like to know?",
        "Good day! I'm here to help you with any HMIS questions."
      ],
      help: [
        "I can help you with patient registration, appointments, reports, billing, and system navigation. What specific topic interests you?",
        "Sure! I'm knowledgeable about all HMIS features. Feel free to ask about any functionality.",
        "I'm here to assist! You can ask me about patients, appointments, reports, inventory, or any other HMIS feature."
      ],
      default: [
        "I understand you're asking about that topic. Let me search for the most relevant information...",
        "That's a great question! Let me find the best answer for you...",
        "I can help with that. Here's what I found..."
      ],
      goodbye: [
        "Thank you for using HMIS Smart Assistant! Have a great day!",
        "Goodbye! Feel free to return anytime you need help with HMIS.",
        "It was my pleasure helping you! Take care!"
      ],
      noMatch: [
        "I couldn't find a specific answer to that question. Could you try rephrasing it or ask about patient management, appointments, reports, or billing?",
        "That's an interesting question, but I don't have that information readily available. Try asking about core HMIS features.",
        "I might not have understood that correctly. Feel free to ask about patient registration, appointments, reports, or other HMIS functions."
      ]
    };
  }

  detectIntent(query) {
    const lowerQuery = query.toLowerCase();
    
    for (const [intent, patterns] of Object.entries(this.intentPatterns)) {
      if (patterns.some(pattern => lowerQuery.includes(pattern))) {
        return intent;
      }
    }
    
    return 'query';
  }

  calculateRelevanceScore(faq, query) {
    const lowerQuery = query.toLowerCase().trim();
    let score = 0;
    
    // Enhanced keyword mapping for better matching
    const keywordMappings = {
      'create': ['create', 'add', 'new', 'make', 'start', 'begin'],
      'user': ['user', 'staff', 'employee', 'person', 'account'],
      'appointment': ['appointment', 'appoint', 'booking', 'schedule'],
      'consultation': ['consultation', 'consult', 'opd', 'visit', 'session'],
      'patient': ['patient', 'person', 'individual', 'case'],
      'bill': ['bill', 'billing', 'invoice', 'payment', 'charge'],
      'doctor': ['doctor', 'physician', 'dr'],
      'register': ['register', 'registration', 'enroll', 'admit'],
      'prescription': ['prescription', 'medicine', 'drug', 'medication'],
      'role': ['role', 'permission', 'access', 'rights']
    };

    // Normalize query words
    const queryWords = lowerQuery.split(/\s+/).filter(word => word.length > 2);
    
    // Exact question match (highest score)
    if (faq.question.toLowerCase().includes(lowerQuery)) {
      score += 100;
    }
    
    // Check for exact keyword matches
    faq.keywords.forEach(keyword => {
      if (lowerQuery.includes(keyword.toLowerCase())) {
        score += 50;
      }
    });
    
    // Check enhanced keyword mappings
    queryWords.forEach(queryWord => {
      Object.entries(keywordMappings).forEach(([key, synonyms]) => {
        if (synonyms.includes(queryWord)) {
          faq.keywords.forEach(faqKeyword => {
            if (faqKeyword.toLowerCase() === key || synonyms.includes(faqKeyword.toLowerCase())) {
              score += 30;
            }
          });
        }
      });
    });
    
    // Individual word matching in keywords
    queryWords.forEach(word => {
      faq.keywords.forEach(keyword => {
        if (keyword.toLowerCase().includes(word) || word.includes(keyword.toLowerCase())) {
          score += 15;
        }
      });
    });
    
    // Question content match
    queryWords.forEach(word => {
      if (faq.question.toLowerCase().includes(word)) {
        score += 10;
      }
    });
    
    // Answer content match (lower priority)
    queryWords.forEach(word => {
      if (faq.answer.toLowerCase().includes(word)) {
        score += 5;
      }
    });
    
    // Priority boost
    if (faq.priority === 'high') {
      score += 10;
    } else if (faq.priority === 'medium') {
      score += 5;
    }
    
    return score;
  }

  searchFaqs(query, limit = 3) {
    const scoredFaqs = faqDatabase.map(faq => ({
      ...faq,
      relevanceScore: this.calculateRelevanceScore(faq, query)
    }));
    
    // Set minimum threshold to prevent wrong answers
    const MINIMUM_SCORE_THRESHOLD = 25;
    
    const filteredFaqs = scoredFaqs
      .filter(faq => faq.relevanceScore >= MINIMUM_SCORE_THRESHOLD)
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    // If top result has very high confidence, return only that
    if (filteredFaqs.length > 0 && filteredFaqs[0].relevanceScore >= 80) {
      return filteredFaqs.slice(0, 1);
    }
    
    return filteredFaqs.slice(0, limit);
  }

  generateSmartResponse(query) {
    const intent = this.detectIntent(query);
    
    // Handle specific intents
    if (intent === 'greeting') {
      return {
        type: 'greeting',
        message: this.getRandomResponse('greeting'),
        suggestions: ['How do I register a new patient?', 'How do I schedule an appointment?', 'How do I generate reports?']
      };
    }
    
    if (intent === 'goodbye') {
      return {
        type: 'goodbye',
        message: this.getRandomResponse('goodbye'),
        suggestions: []
      };
    }
    
    if (intent === 'help') {
      return {
        type: 'help',
        message: this.getRandomResponse('help'),
        suggestions: ['Patient Management', 'Appointments', 'Reports', 'Billing', 'Inventory']
      };
    }
    
    // Search for relevant FAQs
    const relevantFaqs = this.searchFaqs(query);
    
    if (relevantFaqs.length > 0) {
      return {
        type: 'faq_response',
        message: this.getRandomResponse('default'),
        faqs: relevantFaqs,
        suggestions: this.generateSuggestions(intent, relevantFaqs)
      };
    }
    
    // No relevant FAQs found
    return {
      type: 'no_match',
      message: this.getRandomResponse('noMatch'),
      suggestions: ['How do I register a patient?', 'How do I book an appointment?', 'How do I generate reports?']
    };
  }

  getRandomResponse(type) {
    const responses = this.responses[type];
    return responses[Math.floor(Math.random() * responses.length)];
  }

  generateSuggestions(intent, faqs) {
    const suggestions = [];
    
    // Add related questions from same category
    if (faqs.length > 0) {
      const category = faqs[0].category;
      const relatedFaqs = faqDatabase.filter(faq => 
        faq.category === category && !faqs.find(f => f.id === faq.id)
      );
      
      suggestions.push(...relatedFaqs.slice(0, 2).map(faq => faq.question));
    }
    
    // Add default suggestions based on intent
    const intentSuggestions = {
      registration: ['How do I update patient information?', 'What is UHID format?'],
      appointment: ['How do I cancel an appointment?', 'How do I reschedule?'],
      billing: ['How do I generate invoices?', 'How do I track payments?'],
      reports: ['How do I print reports?', 'How do I download reports?']
    };
    
    if (intentSuggestions[intent]) {
      suggestions.push(...intentSuggestions[intent]);
    }
    
    return suggestions.slice(0, 3);
  }

  // Analytics methods
  logInteraction(query, response, userId = 'anonymous') {
    const interaction = {
      timestamp: new Date().toISOString(),
      userId,
      query,
      intent: this.detectIntent(query),
      responseType: response.type,
      satisfaction: null // Will be set by user feedback
    };
    
    // Store in localStorage for demo purposes
    const interactions = JSON.parse(localStorage.getItem('hmis_faq_analytics') || '[]');
    interactions.push(interaction);
    localStorage.setItem('hmis_faq_analytics', JSON.stringify(interactions.slice(-100))); // Keep last 100
    
    return interaction;
  }

  getAnalytics() {
    const interactions = JSON.parse(localStorage.getItem('hmis_faq_analytics') || '[]');
    
    const analytics = {
      totalQueries: interactions.length,
      topIntents: {},
      recentQueries: interactions.slice(-10),
      satisfactionRate: 85
    };
    
    // Calculate intent distribution
    interactions.forEach(interaction => {
      analytics.topIntents[interaction.intent] = (analytics.topIntents[interaction.intent] || 0) + 1;
    });
    
    // Calculate satisfaction rate
    const ratedInteractions = interactions.filter(i => i.satisfaction !== null);
    if (ratedInteractions.length > 0) {
      analytics.satisfactionRate = (ratedInteractions.reduce((sum, i) => sum + i.satisfaction, 0) / ratedInteractions.length * 100).toFixed(1);
    }
    
    return analytics;
  }
}

// Create a singleton instance for the app
const smartFaqEngine = new SmartFaqSystem();

// Language mappings
export const languages = {
  en: 'English',
  hi: 'हिंदी'
};

// Enhanced smartFaqEngine with additional methods needed by SmartFaqChatBot
smartFaqEngine.setLanguage = function(language) {
  this.currentLanguage = language;
};

smartFaqEngine.searchFAQ = function(query, language = 'en') {
  // Use the existing searchFaqs method but format for the chatbot
  const results = this.searchFaqs(query, 5);
  
  // Transform results to match expected format
  return results.map(faq => ({
    id: faq.id,
    title: faq.question,
    description: faq.answer,
    steps: faq.steps || [],
    tips: faq.tips || [],
    category: faq.category,
    relevanceScore: faq.relevanceScore
  }));
};

// Enhanced FAQ Database with multilingual support
export const enhancedFaqDatabase = {
  user_management: {
    en: {
      title: "User Management",
      description: "Learn how to create, manage, and assign roles to users in HMIS system.",
      steps: [
        "Navigate to Administration > User Management",
        "Click 'Add New User' button",
        "Fill in user details: Name, Email, Contact Number",
        "Select appropriate role (Admin, Doctor, Nurse, Receptionist, etc.)",
        "Set permissions based on role requirements",
        "Save user profile and send login credentials"
      ],
      tips: [
        "Use strong passwords and enable 2FA for security",
        "Assign minimal required permissions for each role",
        "Regular audit of user access and permissions"
      ]
    },
    hi: {
      title: "उपयोगकर्ता प्रबंधन",
      description: "HMIS सिस्टम में उपयोगकर्ताओं को बनाना, प्रबंधित करना और भूमिकाएं असाइन करना सीखें।",
      steps: [
        "Administration > User Management में जाएं",
        "'Add New User' बटन पर क्लिक करें",
        "उपयोगकर्ता विवरण भरें: नाम, ईमेल, संपर्क नंबर",
        "उपयुक्त भूमिका चुनें (Admin, Doctor, Nurse, Receptionist, आदि)",
        "भूमिका आवश्यकताओं के आधार पर अनुमतियां सेट करें",
        "उपयोगकर्ता प्रोफ़ाइल सेव करें और लॉगिन क्रेडेंशियल भेजें"
      ],
      tips: [
        "सुरक्षा के लिए मजबूत पासवर्ड और 2FA का उपयोग करें",
        "प्रत्येक भूमिका के लिए न्यूनतम आवश्यक अनुमतियां असाइन करें",
        "उपयोगकर्ता पहुंच और अनुमतियों का नियमित ऑडिट"
      ]
    }
  },

  doctor_role: {
    en: {
      title: "Doctor Role Management",
      description: "Configure doctor profiles, specializations, and consultation settings.",
      steps: [
        "Go to Administration > Doctor Management",
        "Create new doctor profile with medical license details",
        "Set specialization and qualifications",
        "Configure consultation fees and timings",
        "Assign OPD rooms and schedules",
        "Enable appointment booking for the doctor"
      ],
      tips: [
        "Verify medical license numbers before creating profiles",
        "Set appropriate consultation fees based on specialization",
        "Maintain updated contact information for doctors"
      ]
    },
    hi: {
      title: "डॉक्टर भूमिका प्रबंधन",
      description: "डॉक्टर प्रोफाइल, विशेषज्ञता और परामर्श सेटिंग्स कॉन्फ़िगर करें।",
      steps: [
        "Administration > Doctor Management में जाएं",
        "मेडिकल लाइसेंस विवरण के साथ नया डॉक्टर प्रोफाइल बनाएं",
        "विशेषज्ञता और योग्यता सेट करें",
        "परामर्श फीस और समय कॉन्फ़िगर करें",
        "OPD कमरे और शेड्यूल असाइन करें",
        "डॉक्टर के लिए अपॉइंटमेंट बुकिंग सक्षम करें"
      ],
      tips: [
        "प्रोफाइल बनाने से पहले मेडिकल लाइसेंस नंबर सत्यापित करें",
        "विशेषज्ञता के आधार पर उपयुक्त परामर्श फीस सेट करें",
        "डॉक्टरों के लिए अपडेटेड संपर्क जानकारी बनाए रखें"
      ]
    }
  },

  patient_registration: {
    en: {
      title: "Patient Registration",
      description: "Complete guide to register new patients and manage patient records in HMIS.",
      steps: [
        "Navigate to Patient Management > New Registration",
        "Fill mandatory fields: Name, Age, Gender, Contact",
        "Enter address details with PIN code",
        "Add emergency contact information",
        "Generate unique UHID for the patient",
        "Print patient card with UHID and details"
      ],
      tips: [
        "Always verify contact numbers and addresses",
        "Take patient photo for identification",
        "Keep emergency contact details updated"
      ]
    },
    hi: {
      title: "मरीज़ पंजीकरण",
      description: "HMIS में नए मरीज़ों को पंजीकृत करने और मरीज़ रिकॉर्ड प्रबंधित करने की पूरी गाइड।",
      steps: [
        "Patient Management > New Registration में जाएं",
        "अनिवार्य फ़ील्ड भरें: नाम, आयु, लिंग, संपर्क",
        "PIN कोड के साथ पता विवरण दर्ज करें",
        "आपातकालीन संपर्क जानकारी जोड़ें",
        "मरीज़ के लिए अद्वितीय UHID जेनरेट करें",
        "UHID और विवरण के साथ मरीज़ कार्ड प्रिंट करें"
      ],
      tips: [
        "हमेशा संपर्क नंबर और पते सत्यापित करें",
        "पहचान के लिए मरीज़ की फोटो लें",
        "आपातकालीन संपर्क विवरण अपडेटेड रखें"
      ]
    }
  },

  consultation_management: {
    en: {
      title: "Consultation Management",
      description: "Manage doctor consultations, diagnosis, prescriptions, and follow-ups.",
      steps: [
        "Select patient from appointment list",
        "Start consultation session",
        "Record symptoms and vital signs",
        "Add diagnosis and treatment plan",
        "Generate prescription with medicines",
        "Schedule follow-up if required"
      ],
      tips: [
        "Always record complete patient history",
        "Use standard medical terminology for diagnosis",
        "Verify medicine dosage and allergies"
      ]
    },
    hi: {
      title: "परामर्श प्रबंधन",
      description: "डॉक्टर परामर्श, निदान, प्रिस्क्रिप्शन और फॉलो-अप का प्रबंधन करें।",
      steps: [
        "अपॉइंटमेंट सूची से मरीज़ चुनें",
        "परामर्श सत्र शुरू करें",
        "लक्षण और जीवन संकेत रिकॉर्ड करें",
        "निदान और उपचार योजना जोड़ें",
        "दवाओं के साथ प्रिस्क्रिप्शन जेनरेट करें",
        "आवश्यकतानुसार फॉलो-अप शेड्यूल करें"
      ],
      tips: [
        "हमेशा पूरा मरीज़ इतिहास रिकॉर्ड करें",
        "निदान के लिए मानक चिकित्सा शब्दावली का उपयोग करें",
        "दवा की खुराक और एलर्जी सत्यापित करें"
      ]
    }
  },

  appointment_booking: {
    en: {
      title: "Appointment Booking",
      description: "Book, manage, and track patient appointments with doctors.",
      steps: [
        "Go to Appointments > New Booking",
        "Search and select patient by UHID or name",
        "Choose doctor and available time slot",
        "Select appointment type (Consultation/Follow-up)",
        "Confirm appointment details",
        "Print appointment slip for patient"
      ],
      tips: [
        "Check doctor availability before booking",
        "Allow buffer time between appointments",
        "Send SMS/email reminders to patients"
      ]
    },
    hi: {
      title: "अपॉइंटमेंट बुकिंग",
      description: "डॉक्टरों के साथ मरीज़ अपॉइंटमेंट बुक करें, प्रबंधित करें और ट्रैक करें।",
      steps: [
        "Appointments > New Booking में जाएं",
        "UHID या नाम से मरीज़ खोजें और चुनें",
        "डॉक्टर और उपलब्ध समय स्लॉट चुनें",
        "अपॉइंटमेंट प्रकार चुनें (Consultation/Follow-up)",
        "अपॉइंटमेंट विवरण कन्फर्म करें",
        "मरीज़ के लिए अपॉइंटमेंट स्लिप प्रिंट करें"
      ],
      tips: [
        "बुकिंग से पहले डॉक्टर की उपलब्धता जांचें",
        "अपॉइंटमेंट के बीच बफर समय रखें",
        "मरीज़ों को SMS/ईमेल रिमाइंडर भेजें"
      ]
    }
  },

  billing_process: {
    en: {
      title: "Billing Process",
      description: "Generate bills, process payments, and manage financial transactions.",
      steps: [
        "Navigate to Billing > New Bill",
        "Select patient and add services/consultations",
        "Apply discounts or insurance if applicable",
        "Calculate total amount with taxes",
        "Process payment (Cash/Card/UPI)",
        "Print receipt and update payment status"
      ],
      tips: [
        "Verify all services before billing",
        "Check insurance eligibility and coverage",
        "Maintain proper payment documentation"
      ]
    },
    hi: {
      title: "बिलिंग प्रक्रिया",
      description: "बिल जेनरेट करें, भुगतान प्रोसेस करें और वित्तीय लेनदेन प्रबंधित करें।",
      steps: [
        "Billing > New Bill में जाएं",
        "मरीज़ चुनें और सेवाएं/परामर्श जोड़ें",
        "यदि लागू हो तो छूट या बीमा लागू करें",
        "करों के साथ कुल राशि की गणना करें",
        "भुगतान प्रोसेस करें (Cash/Card/UPI)",
        "रसीद प्रिंट करें और भुगतान स्थिति अपडेट करें"
      ],
      tips: [
        "बिलिंग से पहले सभी सेवाओं को सत्यापित करें",
        "बीमा योग्यता और कवरेज जांचें",
        "उचित भुगतान दस्तावेज़ीकरण बनाए रखें"
      ]
    }
  },

  pharmacy_management: {
    en: {
      title: "Pharmacy Management",
      description: "Manage medicine inventory, dispensing, and prescription tracking.",
      steps: [
        "Go to Pharmacy > Medicine Management",
        "Maintain inventory with stock levels",
        "Process prescriptions from doctors",
        "Dispense medicines to patients",
        "Update stock levels and expiry dates",
        "Generate pharmacy reports and alerts"
      ],
      tips: [
        "Check medicine expiry dates regularly",
        "Maintain minimum stock levels",
        "Verify prescription authenticity before dispensing"
      ]
    },
    hi: {
      title: "फार्मेसी प्रबंधन",
      description: "दवा इन्वेंटरी, डिस्पेंसिंग और प्रिस्क्रिप्शन ट्रैकिंग का प्रबंधन करें।",
      steps: [
        "Pharmacy > Medicine Management में जाएं",
        "स्टॉक स्तर के साथ इन्वेंटरी बनाए रखें",
        "डॉक्टरों से प्रिस्क्रिप्शन प्रोसेस करें",
        "मरीज़ों को दवाएं वितरित करें",
        "स्टॉक स्तर और समाप्ति तिथियां अपडेट करें",
        "फार्मेसी रिपोर्ट और अलर्ट जेनरेट करें"
      ],
      tips: [
        "दवा की समाप्ति तिथियों की नियमित जांच करें",
        "न्यूनतम स्टॉक स्तर बनाए रखें",
        "वितरण से पहले प्रिस्क्रिप्शन की प्रामाणिकता सत्यापित करें"
      ]
    }
  }
};

// Enhanced smartFaqEngine methods
smartFaqEngine.setContext = function(context) {
  this.currentContext = context;
};

export { smartFaqEngine };
export default SmartFaqSystem; 